import { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../api';
import WeatherStrip from '../components/WeatherStrip';
import SignRouteMap from '../components/SignRouteMap';
import { useAuth } from '../../context/AuthContext';
import { useIsMobile, StickyAction, Icon, Dialog } from '../../mobile';

// ============================================================
// Tagging Schedule Board — the Tagging Manager's day planner.
// Left: unscheduled queue grouped by region (emergencies → bounced-back
// → oldest within each). Right: the day plan in stop order with ↑↓
// reorder, tech assignment, Save Draft / Publish. ⚡ Build Day proposes
// an optimized route from the queue, anchored to a base region.
// ============================================================

function fmtDateLong(dateStr) {
  return new Date(`${dateStr}T12:00:00Z`).toLocaleDateString('en-US', {
    weekday: 'long', month: 'long', day: 'numeric', timeZone: 'UTC',
  });
}
function fmtHM(mins) {
  if (!mins && mins !== 0) return '—';
  const h = Math.floor(mins / 60), m = mins % 60;
  if (h === 0) return `${m}m`;
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
}
function addDays(dateStr, n) {
  const d = new Date(`${dateStr}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}
function weekdayOf(dateStr) {
  return new Date(`${dateStr}T12:00:00Z`).getUTCDay();
}
// Walk to the nearest service day (union of all regions) in a direction.
function nextServiceDay(from, dir, serviceWeekdays) {
  let d = from;
  for (let i = 0; i < 14; i++) {
    d = addDays(d, dir);
    if (serviceWeekdays.includes(weekdayOf(d))) return d;
  }
  return from;
}

// ── Drive model (mirror of server/services/taggingRouteOptimizer.js — keep
// in sync) so the Day Plan KPIs update live as stops are reordered. ────
const ROAD_FACTOR = 1.3;
function milesBetween(a, b) {
  const toRad = (deg) => (deg * Math.PI) / 180;
  const R = 3958.8;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const s = Math.sin(dLat / 2) ** 2
    + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(s));
}
function driveMinutes(miles) {
  if (miles <= 0.05) return 0;
  const road = miles * ROAD_FACTOR;
  const mph = Math.min(50, 20 + road * 2.2);
  return Math.round((road / mph) * 60) + 4;
}
// Loop KPIs for the day as currently ordered: shop → stops → shop.
function dayKpis(shop, stops) {
  const pts = stops.filter(s => s.lat != null && s.lng != null);
  if (!shop || shop.lat == null || pts.length === 0) return null;
  let miles = 0, driveMin = 0, at = shop;
  for (const s of pts) {
    const d = milesBetween(at, s);
    miles += d; driveMin += driveMinutes(d); at = s;
  }
  const home = milesBetween(at, shop);
  miles += home; driveMin += driveMinutes(home);
  return { drive_minutes: driveMin, road_miles: Math.round(miles * ROAD_FACTOR * 10) / 10 };
}

// ── Visual system ───────────────────────────────────────────
const REGION_THEMES = {
  'Fort Worth': { dot: '#d97706', bg: '#fef3c7', fg: '#92400e' },
  'Dallas':     { dot: '#2563eb', bg: '#dbeafe', fg: '#1e40af' },
  'Denton':     { dot: '#059669', bg: '#d1fae5', fg: '#065f46' },
};
const OTHER_THEME = { dot: '#64748b', bg: '#f1f5f9', fg: '#334155' };
const regionTheme = (name) => REGION_THEMES[name] || OTHER_THEME;

const chip = (bg, color) => ({
  fontSize: 10.5, fontWeight: 700, padding: '2px 8px', borderRadius: 999,
  background: bg, color, textTransform: 'uppercase', letterSpacing: '0.03em',
  whiteSpace: 'nowrap', lineHeight: '16px',
});

function RegionChip({ name }) {
  const t = regionTheme(name);
  return <span style={chip(t.bg, t.fg)}>{name}</span>;
}

function StatPill({ label, value }) {
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', gap: 1, padding: '6px 14px',
      background: 'var(--bg-subtle, #f6f8fa)', borderRadius: 10, border: '1px solid var(--border-subtle, #edf2f7)',
    }}>
      <span style={{ fontSize: 15, fontWeight: 800, color: 'var(--text-primary, #0f172a)', lineHeight: 1.2 }}>{value}</span>
      <span style={{ fontSize: 10.5, fontWeight: 600, color: 'var(--text-faint, #94a3b8)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</span>
    </div>
  );
}

// Compact "⋯" overflow for secondary card actions — keeps the card
// footer to one short row at any column width.
function MoreMenu({ items, busy }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);
  const visible = items.filter(Boolean);
  if (visible.length === 0) return null;
  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button className="btn btn-muted" style={{ padding: '2px 8px', fontSize: 12, fontWeight: 800, letterSpacing: 1 }}
        disabled={busy} title="More actions" onClick={() => setOpen(o => !o)}>⋯</button>
      {open && (
        <div className="card" style={{
          position: 'absolute', right: 0, top: 'calc(100% + 4px)', zIndex: 30, minWidth: 190,
          padding: 4, boxShadow: '0 8px 24px rgba(15, 23, 42, 0.18)',
        }}>
          {visible.map((it, i) => (
            <button key={i} className="btn btn-muted" disabled={busy}
              style={{
                display: 'flex', width: '100%', justifyContent: 'flex-start', border: 'none', background: 'transparent',
                padding: '8px 10px', fontSize: 12.5, fontWeight: 600, color: it.danger ? '#b91c1c' : 'var(--text-primary)', borderRadius: 8,
              }}
              onClick={() => { setOpen(false); it.onClick(); }}>
              {it.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// Where a parked sweep stands: overdue, due today, or a countdown. This is the
// "is it due yet or not" answer, on the card itself.
function DueChip({ dueDate, daysUntil }) {
  const d = Number.isFinite(daysUntil) ? daysUntil : null;
  const pretty = new Date(`${dueDate}T12:00:00Z`).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', timeZone: 'UTC',
  });
  if (d === null) return <span style={chip('#e0f2fe', '#075985')}>Due {pretty}</span>;
  if (d < 0)   return <span style={chip('#fee2e2', '#991b1b')} title={`Was due ${dueDate}`}>⚠ Overdue by {Math.abs(d)}d</span>;
  if (d === 0) return <span style={chip('#dcfce7', '#166534')} title={`Due ${dueDate}`}>● Due today</span>;
  if (d <= 7)  return <span style={chip('#fef3c7', '#92400e')} title={`Due ${dueDate}`}>Due in {d}d · {pretty}</span>;
  return <span style={chip('var(--bg-inset, #edf0f4)', 'var(--text-secondary, #334155)')} title={`Due ${dueDate}`}>Due {pretty} · {d}d</span>;
}

function StopCard({ r, actions, showAge }) {
  const noCoords = r.lat == null || r.lng == null;
  return (
    <div className="card" style={{
      padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 6,
      borderLeft: r.is_emergency ? '3px solid #dc2626' : '3px solid transparent',
      boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
    }}>
      <div style={{ minWidth: 0 }}>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
          <Link to={`/sales/requests/tagging_request/${r.id}`}
            style={{ fontWeight: 700, fontSize: 13.5, color: 'var(--text-primary, #0f172a)', textDecoration: r.status === 'cancelled' ? 'line-through' : 'none' }}>
            {r.account_name || `Request #${r.id}`}
          </Link>
          <span style={{ fontSize: 11.5, color: 'var(--text-faint, #94a3b8)', fontWeight: 600 }}>#{r.id}</span>
          {r.status === 'completed' ? <span style={chip('#d1fae5', '#065f46')}>✓ Completed</span> : null}
          {r.status === 'cancelled' ? <span style={chip('#f1f5f9', '#64748b')}>✕ Cancelled</span> : null}
          {r.is_duplicate ? <span style={chip('#ede9fe', '#5b21b6')} title="Another open request exists for this property — merge them">🔗 Duplicate</span> : null}
          {r.is_emergency && r.status !== 'completed' ? <span style={chip('#fee2e2', '#991b1b')}>Emergency</span> : null}
          {r.attempt_count > 0 && r.status !== 'completed' ? <span style={chip('#fef3c7', '#92400e')}>Returned ×{r.attempt_count}</span> : null}
          {r.tagging_frequency && r.tagging_frequency !== 'single'
            ? <span style={chip('#e0f2fe', '#075985')} title={`Standing ${r.tagging_frequency} sweep — visit #${r.tagging_occurrence}`}>
                🔁 {r.tagging_frequency === 'monthly' ? 'Monthly' : 'Quarterly'} · #{r.tagging_occurrence}
              </span>
            : null}
          {r.tagging_due_date ? <DueChip dueDate={r.tagging_due_date} daysUntil={r.days_until_due} /> : null}
        </div>
        <div style={{ fontSize: 12.5, color: 'var(--text-secondary, #334155)', marginTop: 3, lineHeight: 1.45 }}>{r.tagging_work_summary}</div>
        <div style={{
          display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', marginTop: 6,
        }}>
          <RegionChip name={r.tagging_region} />
          <span style={chip('var(--bg-inset, #edf0f4)', 'var(--text-secondary, #334155)')}>{fmtHM(r.estimated_minutes)}</span>
          {showAge && r.days_waiting >= 7
            ? <span style={chip('#fee2e2', '#991b1b')}>{r.days_waiting}d waiting</span>
            : showAge && r.days_waiting >= 3
              ? <span style={chip('#fef3c7', '#92400e')}>{r.days_waiting}d</span>
              : null}
          {showAge && noCoords
            ? <span style={chip('#f1f5f9', '#64748b')} title="No map coordinates yet — Build Day can't place this job; it geocodes automatically, or add it to a day manually">No map pin</span>
            : null}
          <span style={{ fontSize: 11.5, color: 'var(--text-muted, #64748b)' }}>
            {r.work_address || 'No address'} · {r.submitter_name || '—'}
            {r.tagging_rep_name ? <span style={{ color: '#1e40af', fontWeight: 600 }}> · Rep: {r.tagging_rep_name}</span> : null}
          </span>
        </div>
      </div>
      {actions ? (
        <div style={{
          display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center', justifyContent: 'flex-end',
          paddingTop: 6, borderTop: '1px solid var(--border-subtle, #edf2f7)',
        }}>{actions}</div>
      ) : null}
    </div>
  );
}

// ── Jump-to calendar popover ────────────────────────────────
// Month grid with per-day job counts; arrows still work for the
// day-by-day walk, this is the "go straight to it" path.
function CalendarPopover({ anchorDate, serviceWeekdays, today, onPick, onClose }) {
  const [month, setMonth] = useState(anchorDate.slice(0, 7)); // YYYY-MM
  const [counts, setCounts] = useState({});

  useEffect(() => {
    let live = true;
    api.getTaggingMonth(month)
      .then(res => { if (live) setCounts(Object.fromEntries(res.days.map(d => [d.date, d]))); })
      .catch(() => { });
    return () => { live = false; };
  }, [month]);

  const [y, m] = month.split('-').map(Number);
  const first = new Date(Date.UTC(y, m - 1, 1));
  const daysInMonth = new Date(Date.UTC(y, m, 0)).getUTCDate();
  const lead = first.getUTCDay(); // 0=Sun
  const cells = [
    ...Array.from({ length: lead }, () => null),
    ...Array.from({ length: daysInMonth }, (_, i) => `${month}-${String(i + 1).padStart(2, '0')}`),
  ];
  const monthLabel = first.toLocaleDateString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' });
  const shiftMonth = (dir) => {
    const d = new Date(Date.UTC(y, m - 1 + dir, 1));
    setMonth(`${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`);
  };

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 40 }} />
      <div style={{
        position: 'absolute', top: '110%', left: 0, zIndex: 50, width: 308,
        background: 'var(--bg-elevated, #fff)', borderRadius: 14,
        border: '1px solid var(--border-light, #e2e8f0)',
        boxShadow: '0 12px 32px rgba(15, 23, 42, 0.18)', padding: 14,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
          <button className="btn btn-muted" style={{ padding: '4px 10px' }} onClick={() => shiftMonth(-1)}>‹</button>
          <span style={{ fontWeight: 800, fontSize: 14 }}>{monthLabel}</span>
          <button className="btn btn-muted" style={{ padding: '4px 10px' }} onClick={() => shiftMonth(1)}>›</button>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 3 }}>
          {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, i) => (
            <div key={i} style={{ textAlign: 'center', fontSize: 10, fontWeight: 700, color: 'var(--text-faint, #94a3b8)', padding: '2px 0' }}>{d}</div>
          ))}
          {cells.map((dateStr, i) => {
            if (!dateStr) return <div key={`x${i}`} />;
            const info = counts[dateStr];
            const isService = serviceWeekdays.includes(weekdayOf(dateStr));
            const isSelected = dateStr === anchorDate;
            const isToday = dateStr === today;
            const allDone = info && info.done === info.jobs;
            return (
              <button key={dateStr} onClick={() => onPick(dateStr)}
                style={{
                  border: isSelected ? '2px solid var(--brand-gold, #d97706)' : isToday ? '1.5px solid var(--brand-accent, #3b82f6)' : '1px solid transparent',
                  borderRadius: 8, background: isSelected ? 'var(--brand-gold-glow, rgba(217,119,6,0.10))' : 'transparent',
                  padding: '3px 0 4px', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1,
                  opacity: isService ? 1 : 0.42,
                }}
                title={info ? `${info.jobs} job${info.jobs === 1 ? '' : 's'}${info.done ? ` · ${info.done} done` : ''}` : isService ? 'Service day' : 'Not a usual service day'}>
                <span style={{ fontSize: 12.5, fontWeight: isSelected || isToday ? 800 : 600, color: 'var(--text-primary, #0f172a)' }}>
                  {Number(dateStr.slice(8))}
                </span>
                {info
                  ? <span style={{
                    fontSize: 9, fontWeight: 800, lineHeight: '13px', minWidth: 15, borderRadius: 999, padding: '0 3px',
                    background: allDone ? '#d1fae5' : 'var(--brand-dark, #0b1121)', color: allDone ? '#065f46' : '#fff',
                  }}>{info.jobs}</span>
                  : <span style={{ height: 13 }} />}
              </button>
            );
          })}
        </div>
      </div>
    </>
  );
}

// Complete a driver quick report — the Tagging Manager fills in the real
// form fields (work types, time, emergency) so driver reports end up
// uniform with rep submissions, then the job becomes schedulable.
function CancelModal({ r, busy, onClose, onConfirm }) {
  const [reason, setReason] = useState('');
  const QUICK = ['Rep already handled it', 'Driver already handled it', 'Property withdrew the request', 'Duplicate request'];
  return (
    <Dialog busy={busy} onClose={onClose} maxWidth={460}>
        <h3 style={{ margin: '0 0 2px' }}>Cancel this job</h3>
        <p style={{ margin: '0 0 14px', fontSize: 13, color: 'var(--text-muted)' }}>
          #{r.id} · {r.account_name} — comes off the tagger's route, stays on
          {r.scheduled_date ? ` ${r.scheduled_date}` : ' the record'} as cancelled, and the submitter{r.tagging_rep_name ? ' + rep' : ''} are texted the reason.
        </p>
        <div style={{ fontSize: 12, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-muted)', marginBottom: 6 }}>
          Reason <span style={{ color: '#b91c1c' }}>*</span>
        </div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
          {QUICK.map(q => (
            <button key={q} className="btn btn-muted" style={{ fontSize: 12, padding: '4px 10px', fontWeight: reason === q ? 700 : 500, background: reason === q ? '#fef3c7' : undefined }}
              onClick={() => setReason(q)}>{q}</button>
          ))}
        </div>
        <textarea className="form-input" rows={2} style={{ width: '100%', padding: '9px 12px', marginBottom: 14 }}
          placeholder="Or type your own…" value={reason} onChange={e => setReason(e.target.value)} />
        <div className="m-dialog-actions">
          <button className="btn btn-muted" disabled={busy} onClick={onClose}>Keep it</button>
          <button className="btn btn-gold" style={{ fontWeight: 700, background: '#dc2626', borderColor: '#dc2626' }} disabled={busy || reason.trim().length < 3}
            onClick={() => onConfirm({ reason: reason.trim() })}>
            {busy ? 'Cancelling…' : 'Cancel job'}
          </button>
        </div>
    </Dialog>
  );
}

// Merge duplicates: pick the keeper, the rest fold into it. Everyone who
// submitted or was rep on any of them keeps getting updates.
function MergeModal({ group, busy, onClose, onConfirm }) {
  const [keepId, setKeepId] = useState(group.rows[0].id);
  const others = group.rows.filter(r => r.id !== keepId);
  return (
    <Dialog busy={busy} onClose={onClose} maxWidth={520}>
        <h3 style={{ margin: '0 0 2px' }}>🔗 Merge into one job</h3>
        <p style={{ margin: '0 0 14px', fontSize: 13, color: 'var(--text-muted)' }}>
          {group.rows[0].account_name} — pick which request to keep. The others are cancelled with a pointer;
          their work types, notes and photos fold into the keeper, and every submitter and rep stays on the update list.
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 14 }}>
          {group.rows.map(r => (
            <label key={r.id} style={{
              display: 'flex', gap: 10, alignItems: 'flex-start', padding: '10px 12px', borderRadius: 10, cursor: 'pointer',
              border: `2px solid ${keepId === r.id ? '#7c3aed' : 'var(--border-subtle, #edf2f7)'}`,
              background: keepId === r.id ? '#f5f3ff' : '#fff',
            }}>
              <input type="radio" name="keep" checked={keepId === r.id} onChange={() => setKeepId(r.id)} style={{ marginTop: 3 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 700, fontSize: 13.5 }}>
                  #{r.id} {keepId === r.id && <span style={chip('#ede9fe', '#5b21b6')}>Keep</span>}
                  {r.scheduled_date && <span style={{ ...chip('#e0f2fe', '#075985'), marginLeft: 6 }}>On {r.scheduled_date}</span>}
                </div>
                <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', marginTop: 2 }}>{r.tagging_work_summary}</div>
                <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 2 }}>
                  {fmtHM(r.estimated_minutes)} · by {r.submitter_name || '—'}{r.tagging_rep_name ? ` · rep ${r.tagging_rep_name}` : ''}
                </div>
              </div>
            </label>
          ))}
        </div>
        <div className="m-dialog-actions">
          <button className="btn btn-muted" disabled={busy} onClick={onClose}>Cancel</button>
          <button className="btn btn-gold" style={{ fontWeight: 700 }} disabled={busy || others.length === 0}
            onClick={() => onConfirm(keepId, others.map(o => o.id))}>
            {busy ? 'Merging…' : `Merge ${others.length} into #${keepId}`}
          </button>
        </div>
    </Dialog>
  );
}

// A numbered stop in a route timeline, with the drive leg shown as a
// caption above the card (how the day actually flows).
function TimelineStop({ index, r, leg, actions, last }) {
  const done = r.status === 'completed';
  const cancelled = r.status === 'cancelled';
  return (
    <div style={{ display: 'flex', gap: 12, opacity: cancelled ? 0.55 : done ? 0.85 : 1 }}>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 30, flexShrink: 0 }}>
        {leg && leg.minutes > 0 && <div style={{ width: 2, height: 16, background: 'var(--border-light, #e2e8f0)' }} />}
        <div style={{
          width: 28, height: 28, borderRadius: '50%', flexShrink: 0,
          background: cancelled ? '#94a3b8' : done ? '#059669' : r.is_emergency ? '#dc2626' : 'var(--brand-dark, #0b1121)', color: '#fff',
          display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 12.5,
          boxShadow: '0 1px 3px rgba(15,23,42,0.25)',
        }}>{cancelled ? '✕' : done ? '✓' : index}</div>
        {!last && <div style={{ width: 2, flex: 1, background: 'var(--border-light, #e2e8f0)' }} />}
      </div>
      <div style={{ flex: 1, minWidth: 0, paddingBottom: last ? 0 : 10 }}>
        {leg && leg.minutes > 0 && (
          <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-faint, #94a3b8)', margin: '2px 0 4px' }}>
            {leg.miles} mi · {leg.minutes} min drive
          </div>
        )}
        <StopCard r={r} actions={actions} />
      </div>
    </div>
  );
}

export default function TaggingSchedulePage() {
  const isMobile = useIsMobile();
  const { isSuperAdmin } = useAuth();
  const [date, setDate] = useState('');
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);
  // Local stop order (request ids) + tech pick — saved via Save/Publish
  const [order, setOrder] = useState([]);
  const [techId, setTechId] = useState('');
  const [dirty, setDirty] = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  const [aiRationale, setAiRationale] = useState('');
  // Build Day proposal (preview from the optimizer, nothing written yet)
  const [proposal, setProposal] = useState(null);
  const [building, setBuilding] = useState(false);
  // Driver quick report awaiting real form details (uniformity gate)
  const [cancelFor, setCancelFor] = useState(null);
  const [mergeFor, setMergeFor] = useState(null); // { property_id, rows }
  // Base region for ⚡ Build Day — '' = all regions
  const [buildRegion, setBuildRegion] = useState('');
  // Jump-to calendar + day-plan suggestions
  const [calOpen, setCalOpen] = useState(false);
  const [suggestions, setSuggestions] = useState([]);

  const load = useCallback(async (d) => {
    try {
      const res = await api.getTaggingSchedule(d);
      setData(res);
      setError('');
      // Initialize order from the saved route, appending any day requests
      // not yet on it (rep-booked or added from the queue after last save).
      // Cancelled records stay on the day for history but are NOT part of
      // the plan — keeping them in `order` desynced the ↑/↓ indexes from
      // the rendered list, so a stop after a cancelled one couldn't move.
      const dayIds = res.day.filter(r => r.status !== 'cancelled').map(r => r.id);
      const saved = (res.route?.stops || []).map(s => s.request_id).filter(id => dayIds.includes(id));
      const missing = dayIds.filter(id => !saved.includes(id));
      setOrder([...saved, ...missing]);
      setTechId(res.route?.technician_user_id || res.default_technician_user_id || '');
      setDirty(false);
    } catch (e) {
      setError(e.message);
    }
  }, []);

  useEffect(() => {
    // First load: land on the nearest service day (today if eligible)
    api.getTaggingSchedule(new Date().toLocaleDateString('en-CA'))
      .then(res => {
        const today = res.today;
        const d = res.service_weekdays.includes(weekdayOf(today))
          ? today : nextServiceDay(today, 1, res.service_weekdays);
        setDate(d);
      })
      .catch(e => setError(e.message));
  }, []);

  useEffect(() => { if (date) { setProposal(null); setCalOpen(false); load(date); } }, [date, load]);

  // Day-plan suggestions — recomputed (debounced) whenever the day or the
  // live stop order changes, so reorders update the advice immediately.
  useEffect(() => {
    if (!date || !data) { setSuggestions([]); return; }
    const t = setTimeout(() => {
      api.getTaggingDaySuggestions(date, order)
        .then(res => setSuggestions(res.suggestions || []))
        .catch(() => setSuggestions([]));
    }, 500);
    return () => clearTimeout(t);
  }, [date, data, order]);

  if (error && !data) return <div className="admin-page"><p className="alert alert-error">{error}</p></div>;
  if (!data || !date) return <div className="admin-page"><p>Loading…</p></div>;

  // ── Duplicate detection: same property, more than one open request ──
  const openAll = [...data.queue, ...(data.upcoming || []), ...data.day.filter(r => !['completed', 'cancelled'].includes(r.status))];
  const byProperty = {};
  for (const r of openAll) { if (r.property_id) (byProperty[r.property_id] ||= []).push(r); }
  const dupGroups = Object.values(byProperty).filter(g => g.length > 1);
  const dupPropertyIds = new Set(dupGroups.map(g => g[0].property_id));

  const tagDup = (r) => ({ ...r, is_duplicate: dupPropertyIds.has(r.property_id) && !['completed', 'cancelled'].includes(r.status) });

  const byId = Object.fromEntries(data.day.map(r => [r.id, tagDup(r)]));
  // Live plan = everything the truck actually rolls for. Cancelled records
  // stay attached to the day for history but contribute NOTHING to the
  // map, KPIs, stop numbers, or capacity — they render in a collapsed
  // strip below the plan.
  const orderedDay = order.map(id => byId[id]).filter(Boolean).filter(r => r.status !== 'cancelled');
  // Cancelled stops are removed from the route (no stop_order), so read
  // them straight off the day, not off the route order.
  const cancelledOnDay = data.day.filter(r => r.status === 'cancelled').map(tagDup);
  const capacityPct = Math.min(100, Math.round((data.used_minutes / Math.max(1, data.capacity_minutes)) * 100));
  const published = data.route?.status === 'published';

  // ── Day status — the at-a-glance state of this date ────────
  const dayStatus = published
    ? (dirty
      ? { label: `Published v${data.route.version} — edits pending`, bg: '#ede9fe', fg: '#5b21b6', dot: '#7c3aed' }
      : { label: `Published v${data.route.version}`, bg: '#cffafe', fg: '#0e7490', dot: '#06b6d4' })
    : orderedDay.length > 0
      ? { label: 'Building', bg: '#fef3c7', fg: '#92400e', dot: '#f59e0b' }
      : { label: 'No route yet', bg: '#f1f5f9', fg: '#64748b', dot: '#94a3b8' };

  // ── Day KPIs — live for the current stop order ─────────────
  const dayWorkMinutes = orderedDay.reduce((s, r) => s + (r.estimated_minutes || 0), 0);
  const kpis = dayKpis(data.shop, orderedDay);

  // Reorder against the VISIBLE plan, never the raw id list — anything the
  // plan filters out (cancelled, unknown ids) must not shift the indexes.
  function move(idx, dir) {
    const visible = orderedDay.map(r => r.id);
    const j = idx + dir;
    if (idx < 0 || idx >= visible.length || j < 0 || j >= visible.length) return;
    [visible[idx], visible[j]] = [visible[j], visible[idx]];
    const rest = order.filter(id => !visible.includes(id));
    setOrder([...visible, ...rest]);
    setDirty(true);
  }

  async function act(fn, okMsg) {
    setBusy(true); setNotice(''); setError('');
    try {
      await fn();
      if (okMsg) setNotice(okMsg);
      await load(date);
    } catch (e) {
      setError(e.message);
      await load(date);
    } finally {
      setBusy(false);
    }
  }

  const saveRoute = () => api.saveTaggingRoute({ date, technician_user_id: techId || null, stop_order: orderedDay.map(r => r.id) });

  // ── Proposal derived views ─────────────────────────────────
  const missedEmergencies = (proposal?.skipped || []).filter(s => s.reason === 'emergency_other_region');
  const overBudget = (proposal?.skipped || []).filter(s => s.reason === 'over_budget');
  const noPin = (proposal?.skipped || []).filter(s => s.reason === 'no_coordinates');
  const otherRegion = (proposal?.skipped || []).filter(s => s.reason === 'out_of_region');
  const otherRegionCounts = otherRegion.reduce((m, s) => {
    const k = s.tagging_region || 'Other'; m[k] = (m[k] || 0) + 1; return m;
  }, {});
  const spareMinutes = proposal ? Math.max(0, proposal.stats.budget_minutes - proposal.stats.total_minutes) : 0;
  const baseExhausted = proposal?.base_region && overBudget.length === 0 && spareMinutes >= 45;

  // ── Queue grouped by region ────────────────────────────────
  const knownRegions = data.regions || [];
  const queueGroups = new Map(knownRegions.map(rg => [rg, []]));
  for (const r of data.queue.map(tagDup)) {
    const key = knownRegions.includes(r.tagging_region) ? r.tagging_region : 'Other';
    if (!queueGroups.has(key)) queueGroups.set(key, []);
    queueGroups.get(key).push(r);
  }

  const toolbarBtn = { fontWeight: 600, fontSize: 13, padding: '9px 16px' };

  return (
    <div className="admin-page" style={{ maxWidth: 1240 }}>
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h2 style={{ letterSpacing: '-0.01em' }}>Tagging Schedule</h2>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--text-muted)' }}>
            Build the day, set the stop order, publish to your tagger.
          </p>
        </div>
        <div className={isMobile ? 'm-chips' : ''} style={isMobile ? { width: '100%', marginBottom: 0 } : { display: 'flex', gap: 8 }}>
          <Link to="/sales/tagging-coverage" className="btn btn-muted" style={toolbarBtn}>📊 Coverage</Link>
          <Link to="/sales/tagging-setup" className="btn btn-muted" style={toolbarBtn}>⚙️ Tagging Setup</Link>
          <Link to="/sales/requests/tagging_request" className="btn btn-muted" style={toolbarBtn}>← Tagging Requests</Link>
        </div>
      </div>

      {error && <p className="alert alert-error">{error}</p>}
      {notice && <p className="alert" style={{ background: '#dcfce7', color: '#166534' }}>{notice}</p>}

      <WeatherStrip selectedDate={date} />

      {/* ── Command bar ── */}
      <div className="card" style={{ padding: '14px 18px', marginBottom: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={isMobile
          ? { display: 'flex', flexDirection: 'column', gap: 12, alignItems: 'stretch' }
          : { display: 'flex', gap: 18, alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, position: 'relative', justifyContent: isMobile ? 'space-between' : undefined }}>
            <button className="btn btn-muted" style={{ padding: '7px 12px', borderRadius: 10 }} disabled={busy}
              onClick={() => setDate(nextServiceDay(date, -1, data.service_weekdays))}>‹</button>
            <div style={{ fontWeight: 800, fontSize: 15, minWidth: isMobile ? 0 : 200, flex: isMobile ? 1 : undefined, textAlign: 'center', letterSpacing: '-0.01em' }}>
              {fmtDateLong(date)}
              {date === data.today && <span style={{ ...chip('#e0f2fe', '#075985'), marginLeft: 8, verticalAlign: 'middle' }}>Today</span>}
            </div>
            <button className="btn btn-muted" style={{ padding: '7px 12px', borderRadius: 10 }} disabled={busy}
              onClick={() => setDate(nextServiceDay(date, 1, data.service_weekdays))}>›</button>
            <button className="btn btn-muted" style={{ padding: '7px 12px', borderRadius: 10 }} disabled={busy}
              title="Jump to a date" onClick={() => setCalOpen(o => !o)}>📅</button>
            {calOpen && (
              <CalendarPopover anchorDate={date} serviceWeekdays={data.service_weekdays} today={data.today}
                onPick={d => { setCalOpen(false); if (d !== date) setDate(d); }}
                onClose={() => setCalOpen(false)} />
            )}
          </div>

          <div style={{ flex: 1, minWidth: 200 }}>
            <div style={{
              display: 'flex', justifyContent: 'space-between', fontSize: 11, fontWeight: 700,
              color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4,
            }}>
              <span>Day capacity</span>
              <span style={{ color: capacityPct >= 100 ? '#dc2626' : 'var(--text-secondary)' }}>
                {fmtHM(data.used_minutes)} / {fmtHM(data.capacity_minutes)}
              </span>
            </div>
            <div style={{ height: 7, borderRadius: 999, background: 'var(--bg-inset, #edf0f4)', overflow: 'hidden' }}>
              <div style={{
                width: `${capacityPct}%`, height: '100%', borderRadius: 999, transition: 'width 0.3s ease',
                background: capacityPct >= 100 ? '#dc2626' : capacityPct >= 80 ? '#f59e0b'
                  : 'linear-gradient(90deg, var(--brand-accent, #3b82f6), var(--brand-accent-light, #60a5fa))',
              }} />
            </div>
          </div>

          <select className="form-input" style={{ padding: '9px 12px', minWidth: 180, borderRadius: 10, fontSize: isMobile ? 16 : undefined, minHeight: isMobile ? 48 : undefined }}
            value={techId} onChange={e => { setTechId(e.target.value); setDirty(true); }}>
            <option value="">Assign tagger…</option>
            {data.techs.map(t => <option key={t.id} value={t.id}>{t.full_name}</option>)}
          </select>

          <div style={{
            display: 'flex', alignItems: 'center', gap: 8, padding: '9px 16px', borderRadius: 10,
            background: dayStatus.bg, color: dayStatus.fg, fontWeight: 700, fontSize: 13, whiteSpace: 'nowrap',
          }}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: dayStatus.dot, flexShrink: 0 }} />
            {dayStatus.label}
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', borderTop: '1px solid var(--border-subtle, #edf2f7)', paddingTop: 12 }}>
          <div style={{
            display: 'flex', alignItems: 'stretch', borderRadius: 10, overflow: 'hidden',
            boxShadow: '0 1px 3px rgba(15,23,42,0.12)',
          }}>
            <select className="form-input"
              style={{
                padding: '9px 12px', maxWidth: 150, border: 'none', borderRadius: 0,
                background: 'var(--brand-dark, #0b1121)', color: '#fff', fontWeight: 600, fontSize: 13,
              }}
              title="Base region for the day — the route anchors here; other regions only join when they're on the way"
              value={buildRegion} onChange={e => setBuildRegion(e.target.value)}>
              <option value="">All regions</option>
              {knownRegions.map(rg => <option key={rg} value={rg}>{rg}</option>)}
            </select>
            <button className="btn btn-gold"
              style={{ ...toolbarBtn, fontWeight: 700, borderRadius: 0 }}
              disabled={busy || building || data.queue.length === 0}
              title="Optimizer proposes the most efficient day from the queue — you review before anything is scheduled"
              onClick={async () => {
                setBuilding(true); setError(''); setNotice(''); setProposal(null);
                try {
                  setProposal(await api.buildTaggingDay(date, buildRegion));
                } catch (e) {
                  setError(e.message);
                } finally {
                  setBuilding(false);
                }
              }}>
              {building ? 'Planning…' : '⚡ Build Day'}
            </button>
          </div>
          <button className="btn btn-muted" style={toolbarBtn} disabled={busy || suggesting || orderedDay.length < 2}
            title="Let AI propose a driving order — you still review and publish"
            onClick={async () => {
              setSuggesting(true); setError(''); setNotice(''); setAiRationale('');
              try {
                const s = await api.suggestTaggingOrder(date);
                const proposed = s.ordered_ids.filter(id => order.includes(id));
                const rest = order.filter(id => !proposed.includes(id));
                setOrder([...proposed, ...rest]);
                setDirty(true);
                setAiRationale(s.rationale || 'Order suggested — review and save.');
              } catch (e) {
                setError(e.message);
              } finally {
                setSuggesting(false);
              }
            }}>
            {suggesting ? 'Thinking…' : '✨ Suggest Order'}
          </button>
          <span style={{ flex: 1 }} />
          {!isMobile && <button className="btn btn-muted" style={toolbarBtn} disabled={busy || !dirty}
            onClick={() => act(saveRoute, 'Draft saved')}>Save Draft</button>}
{!isMobile && (
          <button className="btn btn-gold" style={{ ...toolbarBtn, fontWeight: 700 }} disabled={busy || orderedDay.length === 0}
            onClick={() => act(async () => { await saveRoute(); await api.publishTaggingRoute(date); },
              published ? 'Route re-published — tagger sees the new version' : 'Route published — tagger notified')}>
            {published ? `Re-publish (v${data.route.version})` : 'Publish Route'}
          </button>
          )}
        </div>
      </div>

      {isMobile && (
        <StickyAction>
          <button type="button" className="m-btn m-btn-secondary" style={{ width: 'auto', padding: '0 16px', height: 52 }} disabled={busy || !dirty}
            onClick={() => act(saveRoute, 'Draft saved')}>Save</button>
          <button type="button" className="m-btn m-btn-primary" disabled={busy || orderedDay.length === 0}
            onClick={() => act(async () => { await saveRoute(); await api.publishTaggingRoute(date); },
              published ? 'Route re-published — technician sees the new version' : 'Route published — technician notified')}>
            <Icon name="check" size={20} color="#fff" /> {published ? `Re-publish (v${data.route.version})` : 'Publish route'}
          </button>
        </StickyAction>
      )}

      {aiRationale && (
        <p className="alert" style={{ background: '#e0f2fe', color: '#075985' }}>
          ✨ {aiRationale} <span style={{ opacity: 0.75 }}>Review the order, then Save/Publish.</span>
        </p>
      )}
      {published && (
        <p className="alert" style={{ background: '#ede9fe', color: '#5b21b6' }}>
          Published v{data.route.version} · {data.route.published_at?.replace('T', ' ').slice(0, 16)} — changes need a re-publish to reach the tagger.
        </p>
      )}

      {/* ── ⚡ Build Day proposal — preview until Apply ── */}
      {proposal && (
        <div className="card" style={{
          padding: 0, marginBottom: 16, overflow: 'hidden',
          border: '1px solid var(--border-light, #e2e8f0)',
          boxShadow: '0 4px 16px rgba(15, 23, 42, 0.08)',
        }}>
          <div style={{ height: 4, background: 'linear-gradient(90deg, var(--brand-gold, #d97706), var(--brand-gold-light, #f59e0b))' }} />
          <div style={{ padding: '16px 20px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <span style={{ fontWeight: 800, fontSize: 16, letterSpacing: '-0.01em' }}>⚡ Proposed Day</span>
                  {proposal.base_region
                    ? <span style={chip(regionTheme(proposal.base_region).bg, regionTheme(proposal.base_region).fg)}>{proposal.base_region} base</span>
                    : <span style={chip('#f1f5f9', '#334155')}>All regions</span>}
                </div>
                <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 3 }}>
                  Starts &amp; ends at {proposal.shop_address}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn btn-muted" style={toolbarBtn} disabled={busy} onClick={() => setProposal(null)}>Dismiss</button>
                <button className="btn btn-gold" style={{ ...toolbarBtn, fontWeight: 700 }} disabled={busy || proposal.stops.length === 0}
                  onClick={() => act(async () => {
                    await api.applyTaggingBuildDay(date, proposal.stops.map(s => s.id));
                    setProposal(null);
                  }, 'Day applied — jobs scheduled, submitters texted. Assign a tagger and publish when ready.')}>
                  ✓ Apply to Day Plan
                </button>
              </div>
            </div>

            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 14 }}>
              <StatPill label="Stops" value={proposal.stats.stop_count} />
              <StatPill label="Work" value={fmtHM(proposal.stats.work_minutes)} />
              <StatPill label="Driving" value={fmtHM(proposal.stats.total_drive_minutes)} />
              <StatPill label="Route" value={`${proposal.stats.total_miles} mi`} />
              <StatPill label="Day total" value={`${fmtHM(proposal.stats.total_minutes)} / ${fmtHM(proposal.stats.budget_minutes)}`} />
            </div>

            {missedEmergencies.length > 0 && (
              <div style={{
                marginTop: 14, padding: '10px 14px', borderRadius: 10,
                background: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b', fontSize: 13,
              }}>
                <strong>🚨 {missedEmergencies.length} emergenc{missedEmergencies.length === 1 ? 'y' : 'ies'} in other regions
                  {' '}not on this route:</strong>{' '}
                {missedEmergencies.map(s => `#${s.id}${s.account_name ? ` ${s.account_name}` : ''} (${s.tagging_region})`).join(' · ')}
                {' '}— build that region's day next, or add them manually.
              </div>
            )}

            {baseExhausted && (
              <div style={{
                marginTop: 14, padding: '10px 14px', borderRadius: 10,
                background: '#eff6ff', border: '1px solid #bfdbfe', color: '#1e40af', fontSize: 13,
              }}>
                {proposal.base_region} queue is fully covered with <strong>{fmtHM(spareMinutes)}</strong> to spare —
                add nearby jobs manually or build another region on the next service day.
              </div>
            )}

            <div style={{ marginTop: 16 }}>
              {proposal.stops.map((s, i) => (
                <TimelineStop key={s.id} index={i + 1} r={s} leg={s.drive_leg}
                  last={i === proposal.stops.length - 1 && !proposal.stats.return_leg?.minutes}
                  actions={s.pinned ? <span style={chip('#ede9fe', '#5b21b6')}>Already on day</span> : null} />
              ))}
              {proposal.stops.length > 0 && proposal.stats.return_leg?.minutes > 0 && (
                <div style={{ display: 'flex', gap: 12 }}>
                  <div style={{ width: 30, display: 'flex', justifyContent: 'center' }}>
                    <div style={{ width: 2, height: 22, background: 'var(--border-light, #e2e8f0)' }} />
                  </div>
                  <div style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--text-faint, #94a3b8)', alignSelf: 'flex-end' }}>
                    ↩ Back to shop — {proposal.stats.return_leg.miles} mi · {proposal.stats.return_leg.minutes} min
                  </div>
                </div>
              )}
            </div>

            {(overBudget.length > 0 || noPin.length > 0 || otherRegion.length > 0) && (
              <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px solid var(--border-subtle, #edf2f7)', fontSize: 12.5, color: 'var(--text-muted)' }}>
                <div style={{ fontWeight: 700, color: 'var(--text-secondary)', marginBottom: 4 }}>
                  Left in queue ({overBudget.length + noPin.length + otherRegion.length})
                </div>
                {overBudget.map(s => (
                  <div key={s.id}>#{s.id}{s.account_name ? ` · ${s.account_name}` : ''} — doesn't fit today's remaining time</div>
                ))}
                {noPin.map(s => (
                  <div key={s.id}>#{s.id}{s.account_name ? ` · ${s.account_name}` : ''} — no map pin yet (geocoding runs automatically)</div>
                ))}
                {otherRegion.length > 0 && (
                  <div>
                    {Object.entries(otherRegionCounts).map(([rg, n]) => `${rg} ×${n}`).join(' · ')} — waiting for their own region's day
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Supply list — frozen at publish time ── */}
      {data.supply && (data.supply.items.length > 0 || data.supply.specials.length > 0) && (
        <div className="card" style={{ padding: '14px 18px', marginBottom: 16 }}>
          <div style={{ fontWeight: 700, marginBottom: 8 }}>🧰 Load List (v{data.supply.version})</div>
          <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap' }}>
            {data.supply.items.map((it, i) => (
              <div key={i} style={{ fontSize: 13 }}>
                <strong>{it.required}</strong> {it.unit !== 'each' ? `${it.unit} ` : '× '}{it.name}
              </div>
            ))}
          </div>
          {data.supply.specials.length > 0 && (
            <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid var(--border-subtle, #edf2f7)' }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#92400e', marginBottom: 4 }}>SPECIAL TAGGING REQUIRED</div>
              {data.supply.specials.map(sp => (
                <div key={sp.request_id} style={{ fontSize: 13 }}>
                  #{sp.request_id} · {sp.property} — {sp.description}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="grid-stack-mobile"
        style={{ display: 'grid', gridTemplateColumns: 'minmax(300px, 1fr) minmax(340px, 1.2fr)', gap: 20, alignItems: 'start' }}>
        {/* ── Queue — grouped by region ── */}
        <div>
          {dupGroups.length > 0 && (
            <div style={{
              marginBottom: 14, padding: '10px 14px', borderRadius: 10,
              background: '#f5f3ff', border: '1px solid #ddd6fe',
            }}>
              <div style={{ fontWeight: 700, fontSize: 13, color: '#5b21b6', marginBottom: 6 }}>
                🔗 {dupGroups.length} propert{dupGroups.length === 1 ? 'y has' : 'ies have'} more than one open request — one truck visit, one job
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {dupGroups.map(g => (
                  <div key={g[0].property_id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5 }}>
                    <span style={{ fontWeight: 600, flex: 1 }}>{g[0].account_name} <span style={{ color: 'var(--text-muted)' }}>· {g.map(x => `#${x.id}`).join(', ')}</span></span>
                    <button className="btn btn-muted" style={{ fontSize: 12, padding: '4px 10px', fontWeight: 700, color: '#5b21b6' }} disabled={busy}
                      onClick={() => setMergeFor({ property_id: g[0].property_id, rows: g })}>Merge into one</button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Upcoming Recurring — standing sweeps whose next visit isn't due
              yet. Real requests, deliberately parked out of the live queue so
              they don't read as work waiting to be done. Each shows exactly
              where it stands, and can be pulled onto the day early. */}
          {(data.upcoming || []).length > 0 && (
            <div style={{
              marginBottom: 20, padding: 14, borderRadius: 12,
              background: '#f0f9ff', border: '1px solid #bae6fd',
            }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, margin: '0 0 10px' }}>
                <h3 style={{ margin: 0, fontSize: 15, letterSpacing: '-0.01em' }}>🔁 Upcoming Recurring</h3>
                <span style={chip('#e0f2fe', '#075985')}>{data.upcoming.length}</span>
              </div>
              <div style={{ fontSize: 12.5, color: '#075985', marginBottom: 10 }}>
                Standing sweeps waiting for their next visit. ⚡ Build Day skips these until they're
                due — add one early only if you want it on this day.
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {data.upcoming.map(r => (
                  <StopCard key={r.id} r={r} actions={
                    <button className="btn btn-muted" style={{ fontSize: 12, whiteSpace: 'nowrap', padding: '6px 10px', fontWeight: 700 }}
                      disabled={busy}
                      title={r.days_until_due > 0 ? `Not due for ${r.days_until_due} more days — adds it to ${date} anyway` : `Add to ${date}`}
                      onClick={() => act(() => api.addToTaggingDay(r.id, date), `#${r.id} added to ${date}`)}>
                      + Add early
                    </button>
                  } />
                ))}
              </div>
            </div>
          )}

          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, margin: '0 0 12px' }}>
            <h3 style={{ margin: 0, fontSize: 16, letterSpacing: '-0.01em' }}>Awaiting Scheduling</h3>
            <span style={chip('var(--bg-inset, #edf0f4)', 'var(--text-secondary, #334155)')}>{data.queue.length}</span>
          </div>
          {data.queue.length === 0 && (
            <div className="card" style={{ padding: 20, color: 'var(--text-muted)', textAlign: 'center', fontSize: 13.5 }}>
              Queue is clear 🎉
            </div>
          )}
          {[...queueGroups].filter(([, rows]) => rows.length > 0).map(([rg, rows]) => {
            const t = regionTheme(rg);
            const emergencies = rows.filter(r => r.is_emergency).length;
            return (
              <div key={rg} style={{ marginBottom: 18 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '0 0 8px' }}>
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: t.dot, flexShrink: 0 }} />
                  <span style={{
                    fontSize: 12, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em',
                    color: 'var(--text-secondary, #334155)',
                  }}>{rg}</span>
                  <span style={chip(t.bg, t.fg)}>{rows.length}</span>
                  {emergencies > 0 && <span style={chip('#fee2e2', '#991b1b')}>🚨 {emergencies}</span>}
                  <span style={{ flex: 1, height: 1, background: 'var(--border-subtle, #edf2f7)' }} />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {rows.map(r => (
                    <StopCard key={r.id} r={r} showAge actions={<>
                      <button className="btn btn-muted" style={{ fontSize: 12, whiteSpace: 'nowrap', padding: '4px 10px', fontWeight: 600 }} disabled={busy}
                        onClick={() => act(() => api.addToTaggingDay(r.id, date), `#${r.id} added to ${date}`)}>
                        + Add to day
                      </button>
                      <MoreMenu busy={busy} items={[
                        { label: 'Cancel request', danger: true, onClick: () => setCancelFor(r) },
                      ]} />
                    </>} />
                  ))}
                </div>
              </div>
            );
          })}
        </div>

      {cancelFor && (
        <CancelModal r={cancelFor} busy={busy} onClose={() => setCancelFor(null)}
          onConfirm={(body) => act(async () => { await api.cancelTaggingRequest(cancelFor.id, body); setCancelFor(null); },
            `#${cancelFor.id} cancelled — submitter${cancelFor.tagging_rep_name ? ' + rep' : ''} texted`)} />
      )}
      {mergeFor && (
        <MergeModal group={mergeFor} busy={busy} onClose={() => setMergeFor(null)}
          onConfirm={(keepId, mergeIds) => act(async () => { await api.mergeTaggingRequests(keepId, mergeIds); setMergeFor(null); },
            `Merged ${mergeIds.length} duplicate${mergeIds.length === 1 ? '' : 's'} into #${keepId}`)} />
      )}

      {/* ── Day plan ── */}
        <div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, margin: '0 0 12px' }}>
            <h3 style={{ margin: 0, fontSize: 16, letterSpacing: '-0.01em' }}>Day Plan</h3>
            <span style={chip('var(--bg-inset, #edf0f4)', 'var(--text-secondary, #334155)')}>
              {orderedDay.length} stop{orderedDay.length === 1 ? '' : 's'}
            </span>
            {orderedDay.some(r => r.status === 'completed') && (
              <span style={chip('#d1fae5', '#065f46')}>
                ✓ {orderedDay.filter(r => r.status === 'completed').length} done
              </span>
            )}
          </div>

          {orderedDay.length > 0 && (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
              <StatPill label="Work" value={fmtHM(dayWorkMinutes)} />
              {kpis && <StatPill label="Drive time" value={fmtHM(kpis.drive_minutes)} />}
              {kpis && <StatPill label="Total miles" value={`${kpis.road_miles} mi`} />}
              <StatPill label="Day total"
                value={`${fmtHM(dayWorkMinutes + (kpis?.drive_minutes || 0))} / ${fmtHM(data.capacity_minutes)}`} />
            </div>
          )}

          {suggestions.length > 0 && !proposal && (
            <div style={{
              marginBottom: 12, padding: '12px 16px', borderRadius: 12,
              background: '#eff6ff', border: '1px solid #bfdbfe',
            }}>
              <div style={{ fontSize: 12, fontWeight: 800, color: '#1e40af', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
                💡 Make this day better
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {suggestions.map((s, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 13, color: '#1e3a5f' }}>
                    <span style={{ color: '#2563eb', lineHeight: '19px' }}>•</span>
                    <span style={{ flex: 1, lineHeight: 1.45 }}>{s.text}</span>
                    {s.type === 'reorder' && (
                      <button className="btn btn-muted" style={{ fontSize: 11.5, padding: '3px 10px', whiteSpace: 'nowrap' }} disabled={busy}
                        onClick={() => {
                          const proposed = s.ordered_ids.filter(id => order.includes(id));
                          setOrder([...proposed, ...order.filter(id => !proposed.includes(id))]);
                          setDirty(true);
                        }}>Apply order</button>
                    )}
                    {s.type === 'add' && (
                      <button className="btn btn-muted" style={{ fontSize: 11.5, padding: '3px 10px', whiteSpace: 'nowrap' }} disabled={busy}
                        onClick={() => act(() => api.addToTaggingDay(s.add_id, date), `#${s.add_id} added to ${date}`)}>+ Add</button>
                    )}
                    {s.type === 'swap' && (
                      <button className="btn btn-muted" style={{ fontSize: 11.5, padding: '3px 10px', whiteSpace: 'nowrap' }} disabled={busy}
                        onClick={() => act(async () => {
                          await api.removeFromTaggingDay(s.remove_id);
                          await api.addToTaggingDay(s.add_id, date);
                        }, `Swapped #${s.remove_id} out for #${s.add_id}`)}>Swap</button>
                    )}
                    {s.type === 'remove' && (
                      <button className="btn btn-muted" style={{ fontSize: 11.5, padding: '3px 10px', whiteSpace: 'nowrap', color: '#b91c1c' }} disabled={busy}
                        onClick={() => act(() => api.removeFromTaggingDay(s.remove_id), `#${s.remove_id} returned to queue`)}>Move out</button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {data.shop && (proposal?.stops?.length > 0 || orderedDay.length > 0) && (
            <div style={{ marginBottom: 14 }}>
              {/* stop_number = position in the day, so a stop we can't
                  plot never renumbers the ones after it. */}
              <SignRouteMap shop={data.shop}
                stops={(proposal ? proposal.stops : orderedDay).map((s, i) => ({ ...s, stop_number: i + 1 }))} />
              {proposal && (
                <div style={{ fontSize: 11.5, fontWeight: 600, color: '#92400e', marginTop: 5 }}>
                  ⚡ Showing the proposed route — Apply to make it the day plan.
                </div>
              )}
            </div>
          )}

          {orderedDay.length === 0 && (
            <div className="card" style={{ padding: 20, color: 'var(--text-muted)', textAlign: 'center', fontSize: 13.5 }}>
              Nothing scheduled — use ⚡ Build Day or add from the queue.
            </div>
          )}
          <div>
            {orderedDay.map((r, i) => (
              <TimelineStop key={r.id} index={i + 1} r={r} last={i === orderedDay.length - 1}
                actions={r.status === 'completed' ? (
                  // Completed by the tech from the field: leave it. Completed
                  // some other way (a rep closed the card): the manager can
                  // cancel it off the day so the truck doesn't roll for it.
                  <>
                    <button className="btn btn-muted" style={{ fontSize: 11, color: '#b91c1c', padding: '2px 8px' }} disabled={busy}
                      title="Take this off the day — it was closed outside the tagger's route"
                      onClick={() => setCancelFor(r)}>
                      Cancel
                    </button>
                    <MoreMenu busy={busy} items={[
                      isSuperAdmin && { label: '🗑 Delete permanently', danger: true, onClick: () => { if (window.confirm(`Permanently delete #${r.id}? This cannot be undone.`)) act(() => api.deleteRequest(r.id), `#${r.id} deleted`); } },
                    ]} />
                  </>
                ) : <>
                  <button className="btn btn-muted" style={{ padding: '2px 7px', fontSize: 11 }} disabled={busy || i === 0} title="Move up" onClick={() => move(i, -1)}>↑</button>
                  <button className="btn btn-muted" style={{ padding: '2px 7px', fontSize: 11 }} disabled={busy || i === orderedDay.length - 1} title="Move down" onClick={() => move(i, 1)}>↓</button>
                  <button className="btn btn-muted" style={{ fontSize: 11, color: '#b91c1c', padding: '2px 8px' }} disabled={busy}
                    title="Cancel — already handled, withdrawn, or a duplicate"
                    onClick={() => setCancelFor(r)}>
                    Cancel
                  </button>
                  <MoreMenu busy={busy} items={[
                    { label: '↩ Send back to queue', onClick: () => act(() => api.removeFromTaggingDay(r.id), `#${r.id} returned to queue`) },
                    isSuperAdmin && { label: '🗑 Delete permanently', danger: true, onClick: () => { if (window.confirm(`Permanently delete #${r.id}? This cannot be undone.`)) act(() => api.deleteRequest(r.id), `#${r.id} deleted`); } },
                  ]} />
                </>}
              />
            ))}
          </div>

          {cancelledOnDay.length > 0 && (
            <details style={{ marginTop: 14 }}>
              <summary style={{
                cursor: 'pointer', fontSize: 12, fontWeight: 700, color: 'var(--text-muted)',
                textTransform: 'uppercase', letterSpacing: '0.05em', listStyle: 'none',
                display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0',
              }}>
                <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#94a3b8' }} />
                Cancelled this day
                <span style={chip('#f1f5f9', '#64748b')}>{cancelledOnDay.length}</span>
                <span style={{ flex: 1, height: 1, background: 'var(--border-subtle, #edf2f7)' }} />
                <span style={{ fontWeight: 500, textTransform: 'none', letterSpacing: 0 }}>show ▾</span>
              </summary>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 6, opacity: 0.7 }}>
                {cancelledOnDay.map(r => (
                  <StopCard key={r.id} r={r} actions={isSuperAdmin ? (
                    <button className="btn btn-muted" style={{ fontSize: 11, color: '#b91c1c', padding: '2px 8px' }} disabled={busy}
                      title="Super admin: permanently delete this cancelled request"
                      onClick={() => { if (window.confirm(`Permanently delete #${r.id}? This cannot be undone.`)) act(() => api.deleteRequest(r.id), `#${r.id} deleted`); }}>
                      🗑 Delete
                    </button>
                  ) : null} />
                ))}
              </div>
            </details>
          )}
        </div>
      </div>
    </div>
  );
}
